/** The app's route registry for the Nucleus assistant's direct_navigate tool.
 *
 * This is the SOURCE OF TRUTH for assistant navigation: the dock sends it with
 * every turn (uiContext.route_registry), and core builds the tool's routing
 * text and validates picked paths against it — so a new page here is
 * immediately navigable by the assistant, no core change. Core keeps a
 * fallback snapshot (features/nucleus/tools/navigation.py) for surfaces that
 * don't send one; keep the two aligned when editing.
 *
 * The shell (@atomic/app-shell `buildRouteRegistry`) emits the live rail tiles
 * itself — Data (with its tabs), Reporting, Digital Twin, Nucleus, Admin, and
 * the Engine modules main.tsx declares (Demand, Supply, Sparkling, Drink Mix)
 * — and appends these EXTRAS: the pages LMNT routes without a tile. An entry
 * here for a path the rail already carries is dropped, so only tile-less
 * pages belong here.
 *
 * Keep it aligned with the router in routes.tsx (paths must exist there), and
 * write `description`/`aliases` for ROUTING: what a user would SAY, not what
 * the page is called.
 */
import type { RouteRegistryEntry } from "@atomic/app-shell"

export type { RouteRegistryEntry }

export const ROUTE_REGISTRY_EXTRAS: RouteRegistryEntry[] = [
  {
    path: "/replenishment",
    label: "Replenishment",
    aliases: ["replenishment module", "balance walk", "walk", "supply walk"],
    description:
      "The replenishment planning module: the Balance Walk tab (inventory walk by 3PL/warehouse/item/week on an engine run), Production (build plan) and Transfers.",
  },
  {
    // Direct-nav only (no tile): the Replenishment page under test, its walk
    // at both production stages. Named here because it is an engine page
    // (enginePages.ts), which puts it in the registry either way.
    path: "/replenishmentsplittest",
    label: "Replenishment Split Test",
    aliases: ["split test", "first pack walk"],
    description: "The Replenishment page with its Balance Walk at both production stages (First Pack and Finished Goods), under test.",
  },
  {
    path: "/balance-walk",
    label: "Data Dependencies",
    aliases: ["dependency tree", "data set dependencies"],
    description:
      "Master-data dependency tree: each data set with its inputs and dependents. Not the inventory balance walk — that is the Replenishment page's Balance Walk tab.",
  },
  {
    path: "/allocation-engine",
    label: "Allocation Engine",
    aliases: ["allocation", "engine runs"],
    description: "Allocation engine runs, inputs, and stocking targets.",
  },
  {
    path: "/bom-explorer",
    label: "BOM Explorer",
    aliases: ["bom", "bill of materials", "sourcing", "recipe explorer", "where is this made"],
    description:
      "Bill-of-materials explorer: pick a SKU and walk its recipe tree with the chosen source "
      + "(make at which co-man, buy, alternate) at every level; pin a different source to see "
      + "the what-if. ?item=<item_key> opens a root.",
  },
  {
    path: "/trackers",
    label: "Tracker",
    aliases: ["plan tracker", "approvals"],
    description: "Plan tracker: uploaded plan versions, comparisons, approvals.",
  },
  {
    path: "/runs/history",
    label: "Run History",
    aliases: ["runs", "run history"],
    description: "History of the Replenishment module's engine runs.",
  },
  {
    path: "/tickets",
    label: "Tickets",
    aliases: ["linear", "issues"],
    description: "Linear tickets for this workspace.",
  },
  {
    // Routed, but off the rail (main.tsx hides the tile), so the shell's
    // nav-derived registry does not carry it.
    path: "/docs",
    label: "Docs",
    aliases: ["documentation", "help"],
    description: "Product documentation.",
  },
]
